import type { ProjectCreationMode } from './core-value';

export interface ProjectCreatedSizeInput {
  width?: number;
  height?: number;
  /** Size preset key, e.g. `auto`, `instagram_post`. */
  size_preset?: string;
  /** Fixed pixel height vs content-driven auto height. */
  height_mode?: 'fixed' | 'auto';
  strict_height?: boolean;
}

export interface ProjectCreatedAttachmentInput {
  attachment_count?: number;
  asset_count?: number;
  skill_count?: number;
  reference_image_count?: number;
  document_count?: number;
  asset_ids?: string[];
  skill_ids?: string[];
  reference_image_ids?: string[];
  document_ids?: string[];
}

/** Caller input — `buildProjectCreatedProps` fills gaps by parsing prompt markers. */
export interface ProjectCreatedTrackingInput {
  /** Full prompt submitted to create API (includes @Veeso* markers). */
  prompt_content: string;
  creation_mode: ProjectCreationMode;
  account_first_project?: boolean;
  scene?: string;
  coding_model?: string;
  visibility?: string;
  batch_count?: number;
  size?: ProjectCreatedSizeInput;
  attachments?: ProjectCreatedAttachmentInput;
}

export interface ProjectCreatedEventProps {
  project_id: string;
  account_first_project?: boolean;
  creation_mode: ProjectCreationMode;
  creation_source?: string;
  scene?: string;
  prompt_content: string;
  prompt_length: number;
  coding_model?: string;
  visibility?: string;
  batch_count?: number;
  width?: number;
  height?: number;
  size_preset?: string;
  height_mode?: 'fixed' | 'auto';
  strict_height?: boolean;
  attachment_count?: number;
  asset_count?: number;
  skill_count?: number;
  reference_image_count?: number;
  document_count?: number;
  /** Pipe-joined asset ids — set by `buildProjectCreatedProps` / flatten step. */
  asset_ids?: string[] | string;
  skill_ids?: string[] | string;
  reference_image_ids?: string[] | string;
  document_ids?: string[] | string;
}

type ParsedAttachments = Required<
  Pick<
    ProjectCreatedAttachmentInput,
    'attachment_count' | 'asset_count' | 'skill_count' | 'reference_image_count' | 'document_count'
  >
> & {
  asset_ids: string[];
  skill_ids: string[];
  reference_image_ids: string[];
  document_ids: string[];
};

type ParsedPrompt = {
  scene?: string;
  size: ProjectCreatedSizeInput;
  attachments: ParsedAttachments;
};

const EMPTY_ATTACHMENTS: ParsedAttachments = {
  attachment_count: 0,
  asset_count: 0,
  skill_count: 0,
  reference_image_count: 0,
  document_count: 0,
  asset_ids: [],
  skill_ids: [],
  reference_image_ids: [],
  document_ids: [],
};

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

function parsePromptMarkers(prompt: string): ParsedPrompt {
  const attachments: ParsedAttachments = {
    ...EMPTY_ATTACHMENTS,
    asset_ids: [],
    skill_ids: [],
    reference_image_ids: [],
    document_ids: [],
  };
  let scene: string | undefined;
  const size: ProjectCreatedSizeInput = {};

  const sceneMatch = prompt.match(/@VeesoScene\(\s*['"]([\s\S]*?)['"]\s*\)/);
  if (sceneMatch?.[1]) {
    scene = sceneMatch[1];
  }

  for (const match of prompt.matchAll(/@VeesoUserSkill\(\s*['"]([^'"]+)['"]\s*\)/g)) {
    const id = match[1]?.trim();
    if (!id) continue;
    attachments.skill_ids.push(id);
  }
  attachments.skill_count = attachments.skill_ids.length;

  const materialRegex = /@VeesoMaterial\(\s*({[\s\S]*?})\s*\)/g;
  for (const match of prompt.matchAll(materialRegex)) {
    try {
      const parsed = JSON.parse(match[1].trim()) as { id?: string; type?: string };
      const id = parsed.id?.trim();
      if (parsed.type === 'reference') {
        attachments.reference_image_count += 1;
        if (id) attachments.reference_image_ids.push(id);
      } else {
        attachments.asset_count += 1;
        if (id) attachments.asset_ids.push(id);
      }
    } catch {
      attachments.asset_count += 1;
    }
  }

  const docRegex = /@(Veeso|Seede)Document\(\s*({[\s\S]*?})\s*\)/g;
  for (const match of prompt.matchAll(docRegex)) {
    try {
      const parsed = JSON.parse(match[2].trim()) as { id?: string };
      const id = parsed.id?.trim();
      if (!id) continue;
      attachments.document_ids.push(id);
    } catch {
      // ignore malformed marker
    }
  }
  attachments.document_count = attachments.document_ids.length;

  attachments.asset_ids = uniqueIds(attachments.asset_ids);
  attachments.skill_ids = uniqueIds(attachments.skill_ids);
  attachments.reference_image_ids = uniqueIds(attachments.reference_image_ids);
  attachments.document_ids = uniqueIds(attachments.document_ids);

  attachments.attachment_count =
    attachments.asset_count +
    attachments.skill_count +
    attachments.reference_image_count +
    attachments.document_count;

  const sizeMatch = prompt.match(
    /@VeesoCanvasSize\(width:\s*'(\d+)',\s*height:\s*'(\d+)',\s*strictHeight:\s*'(true|false)'\s*\)/
  );
  if (sizeMatch) {
    size.width = Number.parseInt(sizeMatch[1], 10);
    size.height = Number.parseInt(sizeMatch[2], 10);
    size.strict_height = sizeMatch[3] === 'true';
    size.height_mode = size.strict_height ? 'fixed' : 'auto';
  }

  return { scene, size, attachments };
}

/** Summarize landing-page create chatState attachments (pre-serialization). */
export function summarizeCreateChatAttachments(state: {
  assets?: Array<{ loading?: boolean; isMaterial?: boolean; id?: string }>;
  documents?: Array<{ loading?: boolean; id?: string }>;
}): ProjectCreatedAttachmentInput {
  const asset_ids: string[] = [];
  const reference_image_ids: string[] = [];
  const document_ids: string[] = [];

  for (const asset of state.assets || []) {
    if (asset.loading) continue;
    const id = asset.id?.trim();
    if (asset.isMaterial === false) {
      if (id) reference_image_ids.push(id);
    } else if (id) {
      asset_ids.push(id);
    }
  }

  for (const doc of state.documents || []) {
    if (doc.loading) continue;
    const id = doc.id?.trim();
    if (id) document_ids.push(id);
  }

  const asset_count = asset_ids.length;
  const reference_image_count = reference_image_ids.length;
  const document_count = document_ids.length;
  const skill_count = 0;

  return {
    asset_count: asset_count || undefined,
    reference_image_count: reference_image_count || undefined,
    document_count: document_count || undefined,
    skill_count: skill_count || undefined,
    attachment_count:
      asset_count + reference_image_count + document_count + skill_count || undefined,
    asset_ids: asset_ids.length ? uniqueIds(asset_ids) : undefined,
    reference_image_ids: reference_image_ids.length ? uniqueIds(reference_image_ids) : undefined,
    document_ids: document_ids.length ? uniqueIds(document_ids) : undefined,
  };
}

function mergeIds(parsed: string[], explicit?: string[]): string[] {
  if (!explicit?.length) return parsed;
  if (!parsed.length) return uniqueIds(explicit);
  return uniqueIds([...parsed, ...explicit]);
}

function mergeAttachmentCounts(
  parsed: ParsedAttachments,
  explicit?: ProjectCreatedAttachmentInput
): ProjectCreatedAttachmentInput {
  if (!explicit) {
    return omitEmptyAttachments(parsed);
  }

  const asset_ids = mergeIds(parsed.asset_ids, explicit.asset_ids);
  const skill_ids = mergeIds(parsed.skill_ids, explicit.skill_ids);
  const reference_image_ids = mergeIds(parsed.reference_image_ids, explicit.reference_image_ids);
  const document_ids = mergeIds(parsed.document_ids, explicit.document_ids);

  const asset_count = explicit.asset_count ?? asset_ids.length;
  const skill_count = explicit.skill_count ?? skill_ids.length;
  const reference_image_count = explicit.reference_image_count ?? reference_image_ids.length;
  const document_count = explicit.document_count ?? document_ids.length;

  return omitEmptyAttachments({
    asset_count,
    skill_count,
    reference_image_count,
    document_count,
    attachment_count:
      explicit.attachment_count ??
      asset_count + skill_count + reference_image_count + document_count,
    asset_ids,
    skill_ids,
    reference_image_ids,
    document_ids,
  });
}

function mergeSize(
  parsed: ProjectCreatedSizeInput,
  explicit?: ProjectCreatedSizeInput
): ProjectCreatedSizeInput {
  if (!explicit) return parsed;
  const merged = { ...parsed, ...explicit };
  if (merged.strict_height !== undefined && merged.height_mode === undefined) {
    merged.height_mode = merged.strict_height ? 'fixed' : 'auto';
  }
  if (merged.height_mode !== undefined && merged.strict_height === undefined) {
    merged.strict_height = merged.height_mode === 'fixed';
  }
  return merged;
}

function omitEmptyAttachments(
  counts: ProjectCreatedAttachmentInput & {
    asset_ids?: string[];
    skill_ids?: string[];
    reference_image_ids?: string[];
    document_ids?: string[];
  }
): ProjectCreatedAttachmentInput {
  const out: ProjectCreatedAttachmentInput = {};
  if (counts.attachment_count) out.attachment_count = counts.attachment_count;
  if (counts.asset_count) out.asset_count = counts.asset_count;
  if (counts.skill_count) out.skill_count = counts.skill_count;
  if (counts.reference_image_count) out.reference_image_count = counts.reference_image_count;
  if (counts.document_count) out.document_count = counts.document_count;
  if (counts.asset_ids?.length) out.asset_ids = counts.asset_ids;
  if (counts.skill_ids?.length) out.skill_ids = counts.skill_ids;
  if (counts.reference_image_ids?.length) out.reference_image_ids = counts.reference_image_ids;
  if (counts.document_ids?.length) out.document_ids = counts.document_ids;
  return out;
}

/** Normalize project-created tracking props from prompt + optional structured overrides. */
export function buildProjectCreatedProps(
  input: ProjectCreatedTrackingInput
): Omit<ProjectCreatedEventProps, 'project_id' | 'creation_source'> {
  const parsed = parsePromptMarkers(input.prompt_content);
  const attachments = mergeAttachmentCounts(parsed.attachments, input.attachments);
  const size = mergeSize(parsed.size, input.size);

  const props: Omit<ProjectCreatedEventProps, 'project_id' | 'creation_source'> = {
    creation_mode: input.creation_mode,
    prompt_content: input.prompt_content,
    prompt_length: input.prompt_content.length,
    ...attachments,
  };

  if (input.account_first_project !== undefined) {
    props.account_first_project = input.account_first_project;
  }
  if (input.scene ?? parsed.scene) {
    props.scene = input.scene ?? parsed.scene;
  }
  if (input.coding_model) props.coding_model = input.coding_model;
  if (input.visibility) props.visibility = input.visibility;
  if (input.batch_count !== undefined) props.batch_count = input.batch_count;
  if (size.width !== undefined) props.width = size.width;
  if (size.height !== undefined) props.height = size.height;
  if (size.size_preset) props.size_preset = size.size_preset;
  if (size.height_mode) props.height_mode = size.height_mode;
  if (size.strict_height !== undefined) props.strict_height = size.strict_height;

  return props;
}
